/**
 * DocViewSchema for Sales Invoice DocType
 * 
 * Schema-driven layout for Sales Invoice detail pages.
 * 
 * Tab structure:
 * - Details: Sales invoice overview, customer, items, taxes, and totals
 * - Meta: System/audit fields
 * 
 * This schema replaces the legacy FormViewConfig approach.
 * All layout changes should be made in this file.
 */

import type { DocViewSchema } from "./types";

export const salesInvoiceDocViewSchema: DocViewSchema = {
  doctype: "Sales Invoice",
  views: {
    experience: {
      style: "shopify",
      
      // All sections available in this view
      sections: [
        // ===== Details Tab Sections =====
        {
          id: "invoice-overview",
          label: "Invoice Overview",
          description: "Sales Invoice identification and key status information",
          layout: "two-column",
          fields: [
            "name",
            "status",
            "posting_date",
            "due_date",
            "company",
          ],
        },
        {
          id: "customer",
          label: "Customer Information",
          description: "Customer and billing details",
          layout: "two-column",
          fields: [
            "customer",
            "customer_name",
            "territory",
            "contact_person",
            "contact_email",
            "contact_mobile",
          ],
        },
        {
          id: "items",
          label: "Items",
          description: "Invoice line items",
          layout: "one-column",
          fields: [
            "items", // Child table (Sales Invoice Item)
          ],
        },
        {
          id: "taxes",
          label: "Taxes and Charges",
          description: "Tax and charge details",
          layout: "one-column",
          fields: [
            "taxes", // Child table (Sales Taxes and Charges)
          ],
        },
        {
          id: "totals",
          label: "Totals",
          description: "Invoice totals and payment information",
          layout: "two-column",
          fields: [
            "net_total",
            "total_taxes_and_charges",
            "grand_total",
            "rounded_total",
            "outstanding_amount",
            "currency",
            "conversion_rate",
          ],
        },
        {
          id: "payment",
          label: "Payment Information",
          description: "Payment terms and references",
          layout: "two-column",
          fields: [
            "payment_terms_template",
            "advances",
            "paid_amount",
            "write_off_amount",
          ],
        },
        {
          id: "status-summary",
          label: "Status Summary",
          description: "Invoice status and document state",
          layout: "two-column",
          fields: [
            "docstatus",
            "status",
          ],
        },
        
        // ===== Meta Tab Sections =====
        {
          id: "audit",
          label: "System Information",
          description: "Audit and system fields",
          layout: "two-column",
          fields: [
            "owner",
            "creation",
            "modified_by",
            "modified",
          ],
        },
      ],
      
      // Tab definitions - organize sections into navigation
      tabs: [
        {
          id: "details",
          label: "Details",
          sectionIds: ["invoice-overview", "customer", "items", "taxes", "totals", "payment", "status-summary"],
        },
        {
          id: "meta",
          label: "Meta",
          sectionIds: ["audit"],
        },
      ],
      
      // Quick actions available in header/toolbar
      // TODO: Implement these actions via Doc Actions DSL
      quickActions: [
        // "submit_sales_invoice",
        // "cancel_sales_invoice",
        // "create_payment_entry",
        // "duplicate_sales_invoice",
      ],
      
      // Connection groups for Connections tab
      connectionGroups: [
        "sales",
        "invoices",
        "payments",
      ],
    },
    
    // System view: minimal config, let meta drive the layout
    system: {
      sections: [
        {
          id: "all_fields",
          label: "All Fields",
          layout: "one-column",
          // Empty fields array = render all meta fields
        },
      ],
    },
  },
};

